import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CardWrapper } from '~/components/cards/CardWrapper';
import { Button, ClipSpinner } from '~/components/ui';
import { isTerminal, useExtractedDocument } from '~/api/documentExtraction';
import { importHandoffOf } from './importHandoff';
import { DocumentProgress } from './DocumentProgress';
import { ImportCard } from './ImportCard';
import { ItemTable } from './ItemTable';

export default function DocumentExtraction() {
  const { t } = useTranslation('document-extraction');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const documentId = searchParams.get('doc') ?? undefined;

  const { document, isLoading, isLoadingItems, isError } =
    useExtractedDocument(documentId);
  const importHandoff = useMemo(
    () => importHandoffOf(document, `/document-extraction?doc=${documentId}`),
    [document, documentId]
  );
  const finished = document !== undefined && isTerminal(document.status);
  const itemsReady = !isLoadingItems && !isError;

  return (
    <div className="flex flex-col gap-6">
      <CardWrapper>
        <Button
          variant="ghost-primary"
          size="sm"
          width="auto"
          className="mb-2"
          onClick={() =>
            navigate(
              documentId
                ? `/my-products/import?doc=${documentId}`
                : '/my-products/import'
            )
          }
        >
          {t('import.back_to_import')}
        </Button>
        <h1 className="mb-1 text-2xl font-semibold text-neut-prim">
          {t('title')}
        </h1>
        <p className="text-sm text-neut-sec">{t('subtitle')}</p>
      </CardWrapper>

      {isError && (
        <CardWrapper>
          <p data-testid="load-error" className="text-sm text-error-700">
            {t('items.load_failed')}
          </p>
        </CardWrapper>
      )}

      {!document && !isLoading && !isError && (
        <CardWrapper>
          <p className="text-sm text-neut-sec">
            {t('import.open_from')}{' '}
            <Button
              variant="ghost-primary"
              size="sm"
              width="auto"
              onClick={() => navigate('/my-products/import')}
            >
              {t('import.smart_import')}
            </Button>
          </p>
        </CardWrapper>
      )}

      {document && (
        <>
          <DocumentProgress document={document} />
          {isLoadingItems && (
            <CardWrapper>
              <div data-testid="items-loading">
                <ClipSpinner color="currentColor" />
              </div>
            </CardWrapper>
          )}
          {finished && itemsReady && (
            <CardWrapper>
              <ImportCard
                items={document.items}
                importHandoff={importHandoff}
                onContinue={(handoff) =>
                  navigate('/my-products/import', { state: handoff })
                }
              />
            </CardWrapper>
          )}
          {itemsReady && (
            <ItemTable
              items={document.items}
              units={document.units}
              supersededItems={
                importHandoff === null
                  ? undefined
                  : importHandoff.supersededItems
              }
            />
          )}
        </>
      )}
    </div>
  );
}
